/**
 * Design tokens → CSS custom properties.
 *
 * Every colour comes from @galley/shared (THEMES via tokensFor, NOTE_STYLES, FOCUS_RING,
 * DANGER_INK), the same source the contrast tests check in CI, so the web app can never drift
 * from the audited palette. Non-colour tokens (spacing, type scale, motion) live here.
 *
 * Selectors: <html data-theme data-mode> drives everything. A subtree may carry its own
 * data-theme (e.g. theme previews) and it picks up that theme in the current mode.
 */
import {
  DANGER_INK,
  DANGER_INK_NIGHT,
  FOCUS_RING,
  FOCUS_RING_NIGHT,
  NOTE_INK,
  NOTE_STYLES,
  THEME_IDS,
  tokensFor,
  type ColorMode,
  type NoteKind,
  type ThemeId,
  type ThemeTokens,
} from '@galley/shared'
import grainDayUrl from './assets/grain-day.png'
import grainNightUrl from './assets/grain-night.png'

export const COLOR_MODES: readonly ColorMode[] = ['day', 'night', 'projector', 'contrast']
export const NOTE_KINDS: readonly NoteKind[] = ['glow', 'grow', 'wonder', 'see', 'think']
export const STYLE_ELEMENT_ID = 'galley-tokens'

type Decl = [name: string, value: string]

function block(selector: string, decls: Decl[]): string {
  return `${selector} {\n${decls.map(([n, v]) => `  ${n}: ${v};`).join('\n')}\n}`
}

/** Mode-level facts that are not colours from the palette. */
function modeDecls(mode: ColorMode): Decl[] {
  const shared: Decl[] = [
    ['color-scheme', mode === 'night' ? 'dark' : 'light'],
    [
      '--grain-image',
      mode === 'day'
        ? `url("${grainDayUrl}")`
        : mode === 'night'
          ? `url("${grainNightUrl}")`
          : 'none',
    ],
  ]
  // Projector and contrast: thicker lines, no texture (docs/research/06 §4.3).
  const heavy = mode === 'projector' || mode === 'contrast'
  // The day focus ring and danger ink reach only ~2:1 on night surfaces; night has twins.
  if (mode === 'night') {
    shared.push(
      ['--focus-ring', FOCUS_RING_NIGHT],
      ['--danger-ink', DANGER_INK_NIGHT],
      ['--on-danger', NOTE_INK],
    )
  }
  return [
    ...shared,
    ['--line-width', heavy ? '3px' : '2px'],
    ['--note-border-width', heavy ? '3px' : '2px'],
    ['--shadow-offset', heavy ? '0px' : '3px'],
  ]
}

function paletteDecls(t: ThemeTokens): Decl[] {
  const decls: Decl[] = [
    ['--surface', t.surface],
    ['--surface-raised', t.surfaceRaised],
    ['--ink', t.ink],
    ['--ink-muted', t.inkMuted],
    ['--accent', t.accent],
    ['--accent-ink', t.accentInk],
    ['--on-accent', t.onAccent],
    // Primary buttons: accent when on-accent passes 4.5:1 on it, else the accent ink.
    ['--button-fill', t.buttonFill],
    ['--accent-2', t.accent2],
    ['--accent-2-ink', t.accent2Ink],
    ['--line', t.line],
    ['--grain-opacity', String(t.grainOpacity)],
  ]
  t.decor.forEach((color, i) => decls.push([`--decor-${i + 1}`, color]))
  return decls
}

function themeModeSelector(theme: ThemeId, mode: ColorMode): string {
  const m = `[data-mode="${mode}"]`
  const th = `[data-theme="${theme}"]`
  return `:root${m}${th},\n:root${m} ${th}`
}

function staticDecls(): Decl[] {
  const notes: Decl[] = NOTE_KINDS.flatMap((kind): Decl[] => {
    const s = NOTE_STYLES[kind]
    return [
      [`--note-${kind}-fill`, s.fill],
      [`--note-${kind}-border`, s.border],
      [`--note-${kind}-border-style`, s.borderStyle],
    ]
  })
  return [
    // Spacing scale: 4 · 8 · 12 · 16 · 24 · 32 · 48 · 64 px, in rem so it follows the user's font size.
    ['--space-1', '0.25rem'],
    ['--space-2', '0.5rem'],
    ['--space-3', '0.75rem'],
    ['--space-4', '1rem'],
    ['--space-5', '1.5rem'],
    ['--space-6', '2rem'],
    ['--space-7', '3rem'],
    ['--space-8', '4rem'],
    ['--radius-sm', '6px'],
    ['--radius-md', '10px'],
    ['--radius-lg', '16px'],
    ['--radius-pill', '999px'],
    ['--target-min', '44px'],
    ['--target-primary', '48px'],
    ['--measure', '68ch'],
    // Families. Fallbacks are metric-close system faces so a slow font never reflows much.
    [
      '--font-body',
      `'Atkinson Hyperlegible Next Variable', 'Atkinson Hyperlegible Next', system-ui, 'Segoe UI', Roboto, Arial, sans-serif`,
    ],
    ['--font-display', `'Fraunces Variable', Fraunces, 'Iowan Old Style', Georgia, serif`],
    [
      '--font-mono',
      `'Atkinson Hyperlegible Mono Variable', 'Atkinson Hyperlegible Mono', ui-monospace, 'Cascadia Mono', Menlo, monospace`,
    ],
    ['--font-note', `'Shantell Sans Variable', 'Shantell Sans', var(--font-body)`],
    // Not defined via --font-body: the Wide reading style points --font-body at this one.
    ['--font-wide', `'Lexend Variable', Lexend, system-ui, 'Segoe UI', Roboto, Arial, sans-serif`],
    // Motion (06 §7.1). Durations drop to 0 under Calm motion (see global.css).
    ['--dur-press', '80ms'],
    ['--dur-quick', '150ms'],
    ['--dur-base', '240ms'],
    ['--dur-emph', '320ms'],
    ['--ease-out', 'cubic-bezier(0.2, 0.8, 0.2, 1)'],
    ['--ease-inout', 'cubic-bezier(0.65, 0, 0.45, 1)'],
    // Constants that themes may never override (like traffic signs).
    ['--focus-ring', FOCUS_RING],
    ['--focus-width', '3px'],
    ['--focus-offset', '2px'],
    ['--danger-ink', DANGER_INK],
    ['--on-danger', '#FFFFFF'],
    ['--highlight', NOTE_STYLES.glow.fill],
    ['--highlight-ink', NOTE_INK],
    ['--note-ink', NOTE_INK],
    ...notes,
  ]
}

/**
 * Type scale per audience (docs/SPEC.md §5.3): students 18px, teachers 16px, the Reader 20px,
 * the projector 32–36px. Public pages share the student scale so first-time visitors on a
 * projector or Chromebook can read them comfortably.
 */
const AUDIENCE_SCALES: Array<[selector: string, decls: Decl[]]> = [
  [
    '[data-audience="teacher"]',
    [
      ['--text-base', '1rem'],
      ['--text-sm', '0.9375rem'],
      ['--text-xs', '0.875rem'],
      ['--text-lg', '1.125rem'],
      ['--text-xl', '1.375rem'],
      ['--text-label', '0.9375rem'],
      ['--text-code', '0.875rem'],
      ['--text-h1', 'clamp(1.75rem, 1.25rem + 1.5vw, 2.25rem)'],
      ['--text-h2', 'clamp(1.375rem, 1.1rem + 0.9vw, 1.75rem)'],
      ['--text-h3', '1.1875rem'],
      ['--text-display', 'clamp(2.5rem, 1.5rem + 3vw, 4rem)'],
      ['--line-height', '1.5'],
    ],
  ],
  [
    ':is([data-audience="student"], [data-audience="public"])',
    [
      ['--text-base', '1.125rem'],
      ['--text-sm', '1rem'],
      ['--text-xs', '0.9375rem'],
      ['--text-lg', '1.25rem'],
      ['--text-xl', '1.5rem'],
      ['--text-label', '1rem'],
      ['--text-code', '0.9375rem'],
      ['--text-h1', 'clamp(1.875rem, 1.3rem + 1.8vw, 2.5rem)'],
      ['--text-h2', 'clamp(1.5rem, 1.15rem + 1.1vw, 2rem)'],
      ['--text-h3', '1.3125rem'],
      ['--text-display', 'clamp(2.75rem, 1.5rem + 4vw, 4.5rem)'],
      ['--line-height', '1.5'],
    ],
  ],
  [
    '[data-audience="reader"]',
    [
      ['--text-base', '1.25rem'],
      ['--text-sm', '1.0625rem'],
      ['--text-xs', '1rem'],
      ['--text-lg', '1.375rem'],
      ['--text-xl', '1.625rem'],
      ['--text-label', '1rem'],
      ['--text-code', '0.9375rem'],
      ['--text-h1', 'clamp(2rem, 1.4rem + 2vw, 2.75rem)'],
      ['--text-h2', 'clamp(1.5rem, 1.2rem + 1vw, 2rem)'],
      ['--text-h3', '1.375rem'],
      ['--text-display', 'clamp(2.75rem, 1.5rem + 4vw, 4.5rem)'],
      ['--line-height', '1.55'],
    ],
  ],
  [
    '[data-audience="projector"]',
    [
      ['--text-base', 'clamp(2rem, 1.2rem + 1.6vw, 2.25rem)'],
      ['--text-sm', '1.75rem'],
      ['--text-xs', '1.625rem'],
      ['--text-lg', '2.5rem'],
      ['--text-xl', '3rem'],
      ['--text-label', '1.75rem'],
      ['--text-code', '1.75rem'],
      ['--text-h1', 'clamp(3.5rem, 2rem + 3vw, 4rem)'],
      ['--text-h2', '3rem'],
      ['--text-h3', '2.5rem'],
      ['--text-display', 'clamp(4.5rem, 3rem + 4vw, 6rem)'],
      ['--line-height', '1.4'],
    ],
  ],
]

/** Paper is white and ink is black on the printed page, whatever the screen shows. */
const PRINT_PAPER: Decl[] = [
  ['--surface', '#FFFFFF'],
  ['--surface-raised', '#FFFFFF'],
  ['--ink', '#000000'],
  ['--ink-muted', '#333333'],
]

/**
 * Print always uses each theme's day palette on white paper: a teacher printing sign-in
 * cards in Night mode must not get light ink on white. The selectors match the mode blocks'
 * specificity and come after them, so they win without !important.
 */
function printCss(): string {
  const blocks = [
    // Night swaps these constants for twins that only read on dark paper; restore the day ones.
    block(':root[data-mode]', [
      ...modeDecls('day'),
      ['--focus-ring', FOCUS_RING],
      ['--danger-ink', DANGER_INK],
      ['--on-danger', '#FFFFFF'],
    ]),
    ...THEME_IDS.map((theme) =>
      block(`:root[data-mode][data-theme="${theme}"],\n:root[data-mode] [data-theme="${theme}"]`, [
        ...paletteDecls(tokensFor(theme, 'day')),
        ...PRINT_PAPER,
      ]),
    ),
  ]
  return `@media print {\n${blocks.join('\n\n')}\n}`
}

/** The complete token stylesheet. Pure: easy to snapshot and to test. */
export function buildTokenCss(): string {
  const parts: string[] = []
  // Defaults before the Comfort provider sets attributes: the Galley theme in day mode.
  parts.push(
    block(':root', [
      ...staticDecls(),
      ...paletteDecls(tokensFor('galley', 'day')),
      ...modeDecls('day'),
    ]),
  )
  for (const mode of COLOR_MODES) {
    parts.push(block(`:root[data-mode="${mode}"]`, modeDecls(mode)))
    for (const theme of THEME_IDS) {
      parts.push(block(themeModeSelector(theme, mode), paletteDecls(tokensFor(theme, mode))))
    }
  }
  for (const [selector, decls] of AUDIENCE_SCALES) parts.push(block(selector, decls))
  parts.push(printCss())
  return parts.join('\n\n')
}

/** Inject (or refresh) the single <style id="galley-tokens"> element. Idempotent. */
export function injectTokens(doc: Document = document): HTMLStyleElement {
  let el = doc.getElementById(STYLE_ELEMENT_ID)
  if (!(el instanceof HTMLStyleElement)) {
    el = doc.createElement('style')
    el.id = STYLE_ELEMENT_ID
    // First in <head> so component CSS (same specificity, later) can build on the variables.
    doc.head.prepend(el)
  }
  el.textContent = buildTokenCss()
  return el as HTMLStyleElement
}

/** Page background for <meta name="theme-color"> (browser chrome on Android/ChromeOS). */
export function surfaceColor(theme: ThemeId, mode: ColorMode): string {
  return tokensFor(theme, mode).surface
}
